import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import classNames from 'clsx';
import { useConsent } from '~/core/consent/ConsentProvider';
import type { ConsentPreferences } from '~/core/consent/choice';
import { stopPostHog } from '~/lib/posthog/client';
import styles from './ConsentBanner.module.css';

export default function ConsentBanner() {
  const {
    choice,
    ready,
    save,
    openPreferences,
    registerPreferencesDialog,
    hasDesktopPrivacyTrigger,
    hasCompactPrivacyTrigger,
  } = useConsent();
  const [analytics, setAnalytics] = useState(false);
  const [diagnostics, setDiagnostics] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  const onSignInPage =
    typeof window !== 'undefined' &&
    window.location.pathname === '/auth/sign-in';

  const showPreferences = useCallback(
    (button: HTMLButtonElement) => {
      opener.current = button;
      setAnalytics(choice?.analytics ?? false);
      setDiagnostics(choice?.diagnostics ?? false);
      if (dialog.current?.showModal) dialog.current.showModal();
      else dialog.current?.setAttribute('open', '');
    },
    [choice],
  );

  useEffect(
    () => registerPreferencesDialog(showPreferences),
    [registerPreferencesDialog, showPreferences],
  );

  const closePreferences = () => {
    if (dialog.current?.open) {
      if (dialog.current.close) dialog.current.close();
      else dialog.current.removeAttribute('open');
      opener.current?.focus();
    }
  };

  const choose = (preferences: ConsentPreferences) => {
    save(preferences);
    if (!preferences.analytics) stopPostHog();
    closePreferences();
  };

  if (!ready) return null;

  return (
    <>
      {!choice && (
        <aside className={styles.banner} aria-label="Privacy choices">
          <div className={styles.copy}>
            <h2>Your privacy. Your call.</h2>
            <p>
              Essential technology keeps DirectorDeck working. With your
              permission, PostHog measures visits and product use and may record
              sessions with form inputs masked. Sentry reports browser errors
              and performance traces and may record the surrounding session.
              Both stay off until you opt in.{' '}
              <Link href="/cookies">Cookie Policy</Link> ·{' '}
              <Link href="/privacy">Privacy Policy</Link>
            </p>
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.reject}
              onClick={() => choose({ analytics: false, diagnostics: false })}
            >
              Reject all
            </button>
            <button
              type="button"
              className={styles.accept}
              onClick={() => choose({ analytics: true, diagnostics: true })}
            >
              Accept all
            </button>
            <button
              type="button"
              className={styles.customize}
              onClick={(event) => openPreferences(event.currentTarget)}
            >
              Customize
            </button>
          </div>
        </aside>
      )}
      {choice &&
        !onSignInPage &&
        (!hasDesktopPrivacyTrigger || !hasCompactPrivacyTrigger) && (
          <button
            type="button"
            className={classNames(styles.reopen, {
              [styles.compactFallbackOnly]: hasDesktopPrivacyTrigger,
              [styles.desktopFallbackOnly]: hasCompactPrivacyTrigger,
              [styles.landingReopen]:
                typeof window !== 'undefined' &&
                window.location.pathname === '/',
            })}
            onClick={(event) => openPreferences(event.currentTarget)}
          >
            Privacy choices
          </button>
        )}
      <dialog
        ref={dialog}
        className={styles.dialog}
        aria-labelledby="consent-dialog-title"
        aria-describedby="consent-dialog-description"
        onClose={() => opener.current?.focus()}
      >
        <div className={styles.dialogContent}>
          <div className={styles.dialogTop}>
            <span>DirectorDeck privacy</span>
            <button
              type="button"
              aria-label="Close privacy choices"
              onClick={closePreferences}
            >
              ×
            </button>
          </div>
          <h2 id="consent-dialog-title">Choose what we use.</h2>
          <p id="consent-dialog-description">
            Only essential technology is on by default. You can change this
            choice at any time.
          </p>
          <div className={styles.category}>
            <div>
              <strong>Essential</strong>
              <p>Sign-in, security, and remembering your choice.</p>
            </div>
            <span>Always on</span>
          </div>
          <label className={styles.category}>
            <div>
              <strong>Analytics</strong>
              <p>
                PostHog usage measurement and session recording with form inputs
                masked.
              </p>
            </div>
            <input
              type="checkbox"
              aria-label="Analytics"
              checked={analytics}
              onChange={(event) => setAnalytics(event.target.checked)}
            />
          </label>
          <label className={styles.category}>
            <div>
              <strong>Error diagnostics</strong>
              <p>
                Sentry browser errors, performance traces, and error replay.
              </p>
            </div>
            <input
              type="checkbox"
              aria-label="Error diagnostics"
              checked={diagnostics}
              onChange={(event) => setDiagnostics(event.target.checked)}
            />
          </label>
          <div className={styles.dialogActions}>
            <button
              type="button"
              className={styles.reject}
              onClick={() => choose({ analytics: false, diagnostics: false })}
            >
              Reject all
            </button>
            <button
              type="button"
              className={styles.customize}
              onClick={() => choose({ analytics, diagnostics })}
            >
              Save choice
            </button>
            <button
              type="button"
              className={styles.accept}
              onClick={() => choose({ analytics: true, diagnostics: true })}
            >
              Accept all
            </button>
          </div>
          <p className={styles.links}>
            Read our <Link href="/cookies">Cookie Policy</Link> and{' '}
            <Link href="/privacy">Privacy Policy</Link>.
          </p>
        </div>
      </dialog>
    </>
  );
}
